import { useCallback, useEffect, useState } from 'react'
import { Prescription } from '@/dto/api/prescription.dto'
import { useParams, useNavigate, createSearchParams } from 'react-router-dom'
import { getPrescriptionById } from '@/services/api.service'
import { PrescriptionDetailsPage } from '@/pages/prescription/prescription-details.page'
import { LayoutComponent } from '@/layout'
import { LoadingComponent } from '@/components/loading/loading.component'
import { trackEvent, trackPharmacyAvailability } from '@/services/analytics.service'

export function PrescriptionPage (): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const [prescription, setPrescription] = useState<Prescription>()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const failPrescriptionRedirect = useCallback((message?: string) => {
    trackEvent('prescription_error', {
      error_message: message ?? 'Prescription not found',
      prescription_id: id
    })

    navigate({
      // Con receta conocida se va a su error, que da trazabilidad en analitica;
      // sin id solo queda el generico.
      pathname: id != null ? `/${id}/error` : '/error',
      search: createSearchParams({
        message: message != null ? message : 'Prescription not found'
      }).toString()
    })
  }, [navigate, id])

  const fetchPrescription = useCallback(async (): Promise<void> => {
    if (id == null) {
      failPrescriptionRedirect()
      return
    }

    setLoading(true)
    try {
      const response = await getPrescriptionById({ prescriptionId: id })

      if ('bookingId' in response) {
        const [pharmacy] = response.pharmacies

        trackEvent('prescription_already_booked', {
          pharmacy_id: pharmacy?.pharmacy.id,
          items_count: pharmacy?.items.length ?? 0
        })

        navigate(`/${id}/success`)

        return
      }

      // is selected the first pharmacy that is not external
      const pharmacies = response.pharmacies

      const counts = {
        total: pharmacies.length,
        internal: pharmacies.filter((p) => !p.pharmacy.isExternal).length,
        external: pharmacies.filter((p) => p.pharmacy.isExternal).length
      }

      trackEvent('prescription_view', {
        total_items: response.totalItems,
        pharmacies_count: counts.total,
        internal_pharmacies: counts.internal,
        external_pharmacies: counts.external,
        has_address: response.address != null
      })

      trackPharmacyAvailability(counts, 'prescription_load', { total_items: response.totalItems })

      for (const pharmacy of pharmacies) {
        if (!pharmacy.pharmacy.isExternal) {
          pharmacy.selected = true
          break
        }
      }

      setPrescription({
        ...response,
        address: Object.keys((response.address != null) || {}).length > 0 ? response.address : undefined,
        pharmacies
      })
    } catch (e: any) {
      const message = e.response?.data?.message ?? 'An error occurred while fetching the prescription'
      failPrescriptionRedirect(message)
    } finally {
      setLoading(false)
    }
  }, [id, navigate, failPrescriptionRedirect])

  useEffect(() => {
    void fetchPrescription()
  }, [id, fetchPrescription])

  if (prescription != null && id != null) {
    return (
      <LayoutComponent>
        <div className='location-content-wrapper'>
          <PrescriptionDetailsPage
            prescriptionId={id}
            prescription={prescription}
            disabled={loading}
          />
        </div>
      </LayoutComponent>
    )
  }

  return (
    <LayoutComponent>
      <LoadingComponent />
    </LayoutComponent>
  )
}
